import { Box, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { ExternalLink, User } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import type { ActivityItemUserSnippetFragment } from '@graphql/__generated__/graphql';
import {
  snippetContainerSx,
  snippetIconColor,
  tooltipProps,
} from '@modules/_shared/components/feed-v2/constants/styles';
import { useLocale } from '@shared/hooks/locale';

export const ACTIVITY_ITEM_USER_SNIPPET_FRAGMENT = gql(/* GraphQL */ `
  fragment ActivityItemUserSnippet on activities {
    id
    user_id
    user {
      id
      first_name
      last_name
      full_name
      is_broker
    }
  }
`);

type ActivityItemUserSnippetProps = {
  activity: ActivityItemUserSnippetFragment;
};

export const ActivityItemUserSnippet = ({
  activity,
}: ActivityItemUserSnippetProps) => {
  const { t } = useLocale();
  const theme = useTheme();

  const user = activity.user;
  if (user == null) {
    return null;
  }

  const name =
    user.full_name ??
    [user.first_name, user.last_name].filter(Boolean).join(' ');
  const userLink = `/users/${user.id}`;

  return (
    <Tooltip title={t('Click to view linked contact')} {...tooltipProps}>
      <Box
        component="a"
        href={userLink}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          ...snippetContainerSx,
          display: 'block',
          textDecoration: 'none',
          color: 'inherit',
        }}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          {/* Avatar placeholder */}
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              flexShrink: 0,
              bgcolor: 'grey.200',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <User
              size={16}
              style={{ color: (theme.vars ?? theme).palette.grey[500] }}
            />
          </Box>

          {/* Content */}
          <Stack sx={{ flex: 1, minWidth: 0 }}>
            <Stack
              direction="row"
              spacing={0.75}
              sx={{ alignItems: 'center', minWidth: 0 }}
            >
              <ExternalLink
                size={12}
                style={{
                  flexShrink: 0,
                  color: (theme.vars ?? theme).palette.grey[600],
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {name || t('Unknown')}
              </Typography>
            </Stack>
            <Stack
              direction="row"
              spacing={0.5}
              sx={{ alignItems: 'center', mt: 0.25 }}
            >
              <User
                size={11}
                color={snippetIconColor}
                style={{ flexShrink: 0 }}
              />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {user.is_broker === true ? t('Agent') : t('Contact')}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </Tooltip>
  );
};
